// 11 §8 — Counting / Unlimited / Paused / Grace / Locking, all from server rows (`pulse`,
// `pause_state`, `ready_state`) and serverNow(), drawn as the mockup's top-bar timer (UI specifics
// 01): the PULSE N gold square, a phase line ("Planning · 0:18 left", "Hurry", "Resolving · step 4 of
// 9", "Waiting · you locked, 1 still planning") and a segmented bar. The rarely used controls
// (window presets, custom, pause, allow pausing) fold into a popover behind the ⏱ button.
import { useEffect, useState } from 'react';
import { Button, Checkbox, HotkeyKbd, Input, Segmented } from '../components';
import { useEncounter } from '../../core/encounter/session';
import { usePlayback } from '../../core/stores/playback';
import { useNames, useParty, usePauseState, usePulse, useReadyRows, useWorld } from '../../core/subscriptions/hooks';
import { rpc } from '../../core/net/reducers';
import { identityHex, tsToMs } from '../../core/net/connection';
import { formatMmSs, secondsUntil, serverNow } from '../../core/time';
import { playSfx } from '../../core/audio';

export type TimerState = 'counting' | 'unlimited' | 'paused' | 'grace' | 'locking' | 'reading';

export function timerState(p: { deadlineAt?: number; graceUntil?: number; locked: boolean }, paused: boolean, now: number, held = false): TimerState {
  if (p.locked) return 'locking';
  if (held) return 'reading';
  if (paused) return 'paused';
  if (p.graceUntil !== undefined && p.graceUntil > now) return 'grace';
  if (p.deadlineAt === undefined) return 'unlimited';
  return 'counting';
}

const WINDOWS: ReadonlyArray<{ value: number; label: string; title?: string }> = [{ value: 60, label: '60 s' }, { value: 120, label: '120 s' }, { value: 180, label: '180 s' }, { value: 0, label: '∞', title: 'Unlimited' }];
const SEGMENTS = 20;
const HURRY_S = 10;

export function PulseTimer() {
  const encounterId = useEncounter((s) => s.encounterId);
  const partyId = useEncounter((s) => s.partyId);
  const worldId = useEncounter((s) => s.worldId);
  const me = useEncounter((s) => s.me);
  const owners = useEncounter((s) => s.owners);
  const controlled = useEncounter((s) => s.controlled);
  const encState = useEncounter((s) => s.state);
  const playing = usePlayback((s) => s.playing);
  const plan = usePlayback((s) => s.plan);
  const stageIndex = usePlayback((s) => s.stageIndex);
  const pulse = usePulse(encounterId);
  const pause = usePauseState(encounterId);
  const ready = useReadyRows(encounterId);
  const party = useParty(partyId);
  const world = useWorld(worldId);
  const names = useNames(worldId);
  const [now, setNow] = useState(serverNow());
  const [custom, setCustom] = useState('');
  const [open, setOpen] = useState(false);
  // the timer's popover sits over the board's top rows: a click anywhere else or Escape puts it away
  // (Escape is taken here, so it does not also deselect the hero)
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => { if (!(e.target as Element | null)?.closest?.('.fk-hud__timer-opts')) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); setOpen(false); } };
    window.addEventListener('pointerdown', away, true);
    window.addEventListener('keydown', esc, true);
    return () => { window.removeEventListener('pointerdown', away, true); window.removeEventListener('keydown', esc, true); };
  }, [open]);
  useEffect(() => { const id = setInterval(() => setNow(serverNow()), 250); return () => clearInterval(id); }, []);

  const deadlineAt = tsToMs(pulse?.deadlineAt);
  const graceUntil = tsToMs(pulse?.graceUntil);
  const view = { locked: (pulse?.locked ?? false) || encState === 1, ...(deadlineAt !== undefined ? { deadlineAt } : {}), ...(graceUntil !== undefined ? { graceUntil } : {}) };
  // a first turn held for the opening's reading is a pause that names no one (combat::hold_opening)
  const held = (pause?.paused ?? false) && !pause?.pausedBy;
  const state = timerState(view, pause?.paused ?? false, now, held);
  const remaining = deadlineAt !== undefined ? secondsUntil(deadlineAt, now) : undefined;
  const readySet = new Set(ready.filter((r) => r.ready).map((r) => identityHex(r.owner)));
  const myReady = readySet.has(me);
  const humans = [...new Set(Object.values(owners))];
  const stillPlanning = humans.filter((h) => !readySet.has(h)).length;
  const isController = controlled.length > 0;
  const isLead = (party && identityHex(party.lead) === me) || (world && identityHex(world.owner) === me);
  const windowS = pulse?.planningWindowS ?? party?.planningWindowS ?? 120;
  // docs/34: the last five seconds tick on the server's clock, so the whole table ticks together
  const tickAt = state === 'counting' && remaining !== undefined && remaining >= 1 && remaining <= 5 && !myReady ? remaining : undefined;
  useEffect(() => {
    if (tickAt === undefined) return;
    playSfx('plan.tick', { rate: 1 + (5 - tickAt) * 0.06, gain: 0.6 + (5 - tickAt) * 0.1 });
    if (tickAt === 1) playSfx('cine.tick', { gain: 0.7 });   // docs/44: the last second lands on a tick-braam
  }, [tickAt]);
  if (!encounterId) return null;

  // Bar: seconds left out of the planning window; unlimited full; paused frozen; resolving = playback progress.
  const max = windowS > 0 ? windowS : 1;
  const left = state === 'counting' || state === 'grace' ? Math.min(max, remaining ?? 0)
    : state === 'paused' || state === 'reading' ? (pause?.remainingMs ? Math.min(max, Math.ceil(pause.remainingMs / 1000)) : max)
    : state === 'unlimited' ? max : 0;
  const hurry = (state === 'counting' || state === 'grace') && remaining !== undefined && remaining < HURRY_S;
  const steps = plan?.stages.length ?? 0;
  const resolving = playing && steps > 0;
  const waiting = !resolving && myReady && stillPlanning > 0 && state !== 'locking';
  const tone = resolving ? 'resolving' : waiting ? 'waiting' : state === 'paused' || state === 'reading' ? 'paused' : hurry ? 'hurry' : 'planning';
  const filled = resolving ? Math.round(((stageIndex + 1) / steps) * SEGMENTS) : Math.ceil((left / max) * SEGMENTS);
  const clock = state === 'unlimited' ? '∞' : formatMmSs(state === 'paused' || state === 'reading' ? Math.ceil((pause?.remainingMs ?? windowS * 1000) / 1000) : remaining ?? 0);

  let label: string, line: string;
  if (resolving) { label = 'Resolving'; line = `step ${Math.min(stageIndex + 1, steps)} of ${steps}, playing back`; }
  else if (state === 'locking') { label = 'Locking'; line = 'plans are in, resolving'; }
  else if (waiting) { label = 'Waiting'; line = `you locked, ${stillPlanning} still planning`; }
  else if (state === 'reading') { label = 'Reading'; line = `${(pulse?.index ?? 1) <= 1 ? 'the clock starts once everyone has read the opening' : 'the clock starts once everyone has watched the turn'} · ${clock}`; }
  else if (state === 'paused') { label = 'Paused'; line = `by ${names[identityHex(pause?.pausedBy)] ?? 'someone'} · ${clock} left`; }
  else if (state === 'unlimited') { label = 'Planning'; line = 'no clock, resolves when all lock'; }
  else if (state === 'grace') { label = 'Planning'; line = `deadline moved, ${secondsUntil(graceUntil ?? now, now)} s grace`; }
  else if (hurry) { label = 'Hurry'; line = `${clock} left`; }
  else { label = 'Planning'; line = `resolves in ${clock}`; }

  return (
    <div className={`fk-hud__timer fk-hud__timer--${tone}`} data-testid="pulse-timer" data-state={state} data-tone={tone} data-pulse-index={pulse?.index ?? 0}>
      <span className="fk-hud__pulse" aria-label={`Turn ${pulse?.index ?? 0}`}><small>Turn</small><b>{pulse?.index ?? '…'}</b></span>
      <span className="fk-hud__phase">
        <span className="fk-hud__phase-line" role="timer" aria-live="off"><b>{label}</b> · {line}</span>
        <span className="fk-hud__segbar" aria-hidden="true">{Array.from({ length: SEGMENTS }, (_, i) => <i key={i} className={i < filled ? 'on' : undefined} />)}</span>
      </span>
      <span className="fk-hud__timer-opts">
        <button type="button" className="fk-hud__iconbtn fk-hit" aria-expanded={open} aria-label="Turn timer options" title="Planning window, pause" onClick={() => setOpen(!open)} data-testid="timer-options"><span aria-hidden="true" className="fk-hud__clockicon" /></button>
        {open && (
          <div className="fk-hud__popover fk-hit" role="group" aria-label="Turn controls">
            <span className="fk-hud__label">Planning window</span>
            <span className="fk-hud__poprow">
              <Segmented label="Planning window" value={windowS} disabled={!isController} options={WINDOWS} onChange={(s) => void rpc.setDeadline(encounterId, s)} />
              <Input small className="fk-hud__custom" aria-label="Custom duration in seconds" inputMode="numeric" placeholder="custom s" value={custom} disabled={!isController} onChange={(e) => setCustom(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && Number(custom) >= 15) { void rpc.setDeadline(encounterId, Number(custom)); setCustom(''); } }} data-testid="custom-window" />
            </span>
            <span className="fk-hud__poprow">
              <Button small disabled={!isController || !(pause?.allowed ?? true) || held} aria-pressed={pause?.paused ?? false} onClick={() => void (pause?.paused ? rpc.resume(encounterId) : rpc.pause(encounterId))} data-testid="pause-button">{pause?.paused ? 'Resume' : 'Pause'} <HotkeyKbd id="board.pause" /></Button>
              <Checkbox className="fk-hud__allow" label="Allow pausing" checked={pause?.allowed ?? true} disabled={!isLead} title={isLead ? undefined : 'lead only'} onChange={(e) => void rpc.setAllowPausing(encounterId, e.target.checked)} />
            </span>
          </div>
        )}
      </span>
    </div>
  );
}
